/**
 * Helpers for the "Contenidos AI" module (batch generation of images and videos)
 */

export const MAX_PROMPT_LENGTH = 4000;
export const MAX_TOTAL_CONTENTS = 20;
export const ASPECT_RATIOS = ['1:1', '4:5', '9:16', '16:9'];
export const DEFAULT_ASPECT_RATIO = '9:16';
export const VIDEO_ASPECT_RATIOS = ['9:16', '16:9'];
export const POLLING_INTERVAL = 10000;

export const MAX_REFERENCES = 4;
export const REFERENCE_KINDS = ['product', 'location', 'person', 'logo', 'other'];
export const REFERENCE_MIME_TYPES = ['image/jpeg', 'image/png'];
export const REFERENCE_MAX_SIZE = 10 * 1024 * 1024;
export const REFERENCE_MIN_SIDE = 300;

export const ACTIVE_REQUEST_STATUSES = ['pending', 'processing'];
export const FAILED_JOB_STATUSES = ['failed', 'cancelled'];
export const REQUEST_STATUS_VARIANTS = {
  pending: 'light-secondary',
  processing: 'light-primary',
  completed: 'light-success',
  partial: 'light-warning',
  failed: 'light-danger',
};

let row_key = 0;

export const newPlanRow = (asset_type = 'image') => ({
  key: row_key++,
  asset_type,
  quantity: 1,
  model_key: null,
  option: null,
});

export const modelKey = (model) => `${model.provider}:${model.model}`;

/**
 * Models that can be chosen in the plan for an asset type.
 * Image models that need an input image are excluded: references are not available yet.
 */
export const modelsForType = (models, asset_type) => models.filter((model) => (
  model.asset_type === asset_type && !(asset_type === 'image' && model.requires_input_image)
));

/**
 * Models with `is_available = false` are shown as coming soon
 */
export const isModelAvailable = (model) => model.is_available !== false;

export const isTypeAvailable = (models, asset_type) => modelsForType(models, asset_type).some(isModelAvailable);

/**
 * Model preselected in a row: the default of an available provider (`is_default` is per provider)
 */
export const defaultModelKey = (models, asset_type) => {
  const available = modelsForType(models, asset_type).filter(isModelAvailable);
  const model = available.find((item) => item.is_default) || available[0];
  return model ? modelKey(model) : null;
};

/**
 * Model chosen in a row (null means the backend default of its type)
 */
export const rowModel = (models, row) => {
  if (!row.model_key) return null;
  return modelsForType(models, row.asset_type).find((model) => modelKey(model) === row.model_key) || null;
};

/**
 * Options of a model: durations for video, qualities for image
 */
export const modelOptions = (model, asset_type) => {
  if (!model) return [];
  return (asset_type === 'video' ? model.durations : model.qualities) || [];
};

/**
 * Options available in a row. Without a chosen model, only the options shared by
 * every default model of the type are offered (the backend picks which default runs).
 */
export const rowOptions = (models, row) => {
  const model = rowModel(models, row);
  if (model) return modelOptions(model, row.asset_type);

  const defaults = modelsForType(models, row.asset_type).filter((item) => item.is_default);
  if (!defaults.length) return [];
  return defaults.reduce((shared, item) => {
    const options = modelOptions(item, row.asset_type);
    return shared.filter((option) => options.includes(option));
  }, modelOptions(defaults[0], row.asset_type));
};

const QUALITY_ORDER = ['low', 'medium', 'high'];

/**
 * Qualities from lowest to highest, durations from shortest to longest
 */
export const sortOptions = (options) => [...options].sort((a, b) => {
  const quality_a = QUALITY_ORDER.indexOf(a);
  const quality_b = QUALITY_ORDER.indexOf(b);
  if (quality_a !== -1 || quality_b !== -1) return (quality_a === -1 ? 99 : quality_a) - (quality_b === -1 ? 99 : quality_b);
  return (parseFloat(a) || 0) - (parseFloat(b) || 0);
});

export const totalContents = (plan) => plan.reduce((total, row) => total + (parseInt(row.quantity) || 0), 0);

/**
 * Body for estimate/create requests.
 * `source` is optional: { type: 'campaign' | 'casting_call', uuid }
 */
export const buildRequestPayload = ({ prompt, aspect_ratio, plan, references = [] }, models, language, source = null) => {
  const payload = {
    prompt: prompt.trim(),
    aspect_ratio,
    plan: plan.map((row) => {
      const item = { asset_type: row.asset_type, quantity: parseInt(row.quantity) || 0 };
      const model = rowModel(models, row);
      if (model) {
        item.provider = model.provider;
        item.model = model.model;
      }
      if (row.option) item.params = { [row.asset_type === 'video' ? 'duration' : 'quality']: row.option };
      return item;
    }),
    language,
  };
  if (references.length) payload.references = references.map((reference) => reference.uuid);
  if (source && source.type && source.uuid) payload[source.type] = source.uuid;
  return payload;
};

/**
 * References a batch accepts: images use them as model input, so the lowest
 * `max_references` among the image rows' models applies (videos only use them for the base image)
 */
export const maxReferences = (models, plan) => plan
  .filter((row) => row.asset_type === 'image')
  .map((row) => rowModel(models, row))
  .filter((model) => model && typeof model.max_references === 'number')
  .reduce((max, model) => Math.min(max, model.max_references), MAX_REFERENCES);

const readImageSize = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    resolve({ width: image.naturalWidth, height: image.naturalHeight });
    URL.revokeObjectURL(url);
  };
  image.onerror = () => {
    resolve(null);
    URL.revokeObjectURL(url);
  };
  image.src = url;
});

/**
 * Checks a reference file before uploading it.
 * Resolves the i18n key of the problem, or null when the file is valid.
 */
export const validateReferenceFile = async (file) => {
  if (!REFERENCE_MIME_TYPES.includes(file.type)) return 'ia.contents.referenceInvalidType';
  if (file.size > REFERENCE_MAX_SIZE) return 'ia.contents.referenceTooLarge';
  const size = await readImageSize(file);
  if (!size) return 'ia.contents.referenceInvalidType';
  if (size.width < REFERENCE_MIN_SIDE || size.height < REFERENCE_MIN_SIDE) return 'ia.contents.referenceTooSmall';
  return null;
};

const toMessages = (value) => {
  if (!value) return [];
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.reduce((messages, item) => messages.concat(toMessages(item)), []);
  if (typeof value === 'object') return Object.values(value).reduce((messages, item) => messages.concat(toMessages(item)), []);
  return [String(value)];
};

/**
 * Normalizes a 400 error from the API wrapper into
 * { general: [], prompt: [], aspect_ratio: [], plan: [], references: [], plan_rows: { index: [] }, no_credits }
 */
export const parseRequestErrors = (error) => {
  const errors = { general: [], prompt: [], aspect_ratio: [], plan: [], references: [], plan_rows: {}, no_credits: false };
  const message = error && error.message;

  if (error && error.code === 'openAI.error') {
    errors.no_credits = true;
    return errors;
  }

  if (!message || typeof message !== 'object' || Array.isArray(message)) {
    errors.general = toMessages(message);
    return errors;
  }

  Object.keys(message).forEach((field) => {
    if (field === 'plan' && Array.isArray(message.plan)) {
      message.plan.forEach((row_error, index) => {
        if (typeof row_error === 'string') errors.plan.push(row_error);
        else if (toMessages(row_error).length) errors.plan_rows[index] = toMessages(row_error);
      });
    } else if (['prompt', 'aspect_ratio', 'plan', 'references'].includes(field)) {
      errors[field] = toMessages(message[field]);
    } else {
      errors.general = errors.general.concat(toMessages(message[field]));
    }
  });
  return errors;
};

export const requestAspectRatio = (request) => (
  (request && request.settings && request.settings.aspect_ratio) || (request && request.aspect_ratio) || DEFAULT_ASPECT_RATIO
);

/**
 * Videos only come out in 9:16 or 16:9; other formats come out vertical
 */
export const jobAspectRatio = (job, aspect_ratio) => {
  if (job.asset_type === 'video' && !VIDEO_ASPECT_RATIOS.includes(aspect_ratio)) return '9:16';
  return aspect_ratio;
};

/**
 * Cards: final jobs ordered by plan_index and variation_index
 */
export const finalJobs = (request) => ((request && request.jobs) || [])
  .filter((job) => !job.is_intermediate)
  .sort((a, b) => (a.plan_index - b.plan_index) || (a.variation_index - b.variation_index));

export const dependencyOf = (request, job) => (
  job.depends_on ? ((request && request.jobs) || []).find((item) => item.uuid === job.depends_on) || null : null
);

export const mainAsset = (job) => {
  const assets = (job && job.assets) || [];
  return assets.find((asset) => !asset.is_intermediate && asset.asset_type === job.asset_type) || assets[0] || null;
};

/**
 * Base image of a video (asset of the intermediate job it depends on)
 */
export const previewAsset = (request, job) => {
  const dependency = dependencyOf(request, job);
  if (!dependency) return null;
  return (dependency.assets || []).find((asset) => asset.asset_type === 'image' && asset.url) || null;
};

export const requestProgress = (request) => {
  const jobs = finalJobs(request);
  return {
    total: jobs.length,
    completed: jobs.filter((job) => job.status === 'completed').length,
    failed: jobs.filter((job) => FAILED_JOB_STATUSES.includes(job.status)).length,
  };
};

/**
 * Applies an `ai_content_updated` websocket event to the request detail.
 * Returns false when the job is unknown (the detail must be fetched again).
 */
export const applyContentEvent = (request, event) => {
  const job = (request.jobs || []).find((item) => item.uuid === event.job_uuid);
  if (event.request_status) request.status = event.request_status;
  if (!job) return false;

  if (event.job_status) job.status = event.job_status;
  if (Array.isArray(event.assets) && event.assets.length) {
    job.assets = event.assets.map((asset) => {
      const previous = (job.assets || []).find((item) => item.uuid === asset.uuid) || {};
      return { ...previous, ...asset, is_intermediate: !!event.is_intermediate };
    });
  }
  return true;
};
